import { Box, withStyles } from 'vopay';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { Title } from '#shared/components/UI';
import SettingsSideMenu from './SettingsSideMenu/SettingsSideMenu';
import SettingsOverview from './SettingsOverview/SettingsOverview';
import useSettingsLockedDialog from './useSettingsLockedDialog';
import { SettingsHostProps, SettingsItem } from './types';
import styles from './SettingsShell.module.scss';

const cn = withStyles(styles);

// The Settings shell: a persistent side menu alongside a content pane. The shell renders the
// overview itself at `basePath`; every other path renders the host's own `children` (its inner
// routes' <Switch>). Built for the Account Portal (ID-13390), extracted here so the Partner
// Portal can mount the same shell with its own card list (ID-13819/ID-13420).
export default function SettingsShell({ title, basePath, items, hostActions, children }: SettingsHostProps) {
  const history = useHistory();
  const { pathname } = useLocation();
  // Not `===`: non-strict exact matching also accepts a trailing slash ('/settings/'), as the
  // host's `exact path="/settings"` route did before the extraction.
  const isOverview = !!matchPath(pathname, { path: basePath, exact: true });
  const showLockedFeatureDialog = useSettingsLockedDialog(hostActions.onRequestAccess);

  // Shared by the side menu and the overview cards so the two entry points can't diverge.
  const onSelectItem = (item: SettingsItem) => {
    if (item.isLocked) {
      showLockedFeatureDialog(item);
    } else {
      history.push(item.path);
    }
  };

  return (
    <Box flx="col" gapSize="32px">
      <Title level="3">{title}</Title>

      <div className={cn('content')}>
        <SettingsSideMenu basePath={basePath} isOverview={isOverview} items={items} onSelectItem={onSelectItem} />

        <div className={cn('contentPane')}>
          {isOverview ? <SettingsOverview items={items} onSelectItem={onSelectItem} /> : children}
        </div>
      </div>
    </Box>
  );
}
